/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslations } from '@/hooks/useTranslations';
import { Variants } from '@/types';
import { formatInputDate } from '@/utils/formatInputDate';
import { formatInputTime } from '@/utils/formatInputTime';
import { formatPhone } from '@/utils/formatPhone';
import cn from 'classnames';
import { ChangeEvent, MouseEvent, ReactNode, useState } from 'react';
import {
  FieldError,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { Button } from '../Button';
import styles from './Input.module.scss';

type InputProps = {
  label?: string;
  name: string;
  error?: FieldError;
  description?: string;
  placeholder: string;
  type?: string;
  register: UseFormRegister<any>;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFirstChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  pattern?: RegExp;
  button?: {
    text: string;
    onClick: () => void;
  };
  className?: string;
  setValue: UseFormSetValue<any>;
  disabled?: boolean;
  content?: ReactNode;
  readOnly?: boolean;
  hideEye?: boolean;
  alert?: string;
  customErrorContent?: ReactNode;
  isBirthday?: boolean;
  revalidateOnBlur?: boolean;
  onlyFuture?: boolean;
  clearErrors?: UseFormClearErrors<any>;
  onClick?: () => void;
  autoComplete?: string;
  hideErrorText?: boolean;
};

export function Input({
  label,
  placeholder,
  register,
  required,
  button,
  type = 'text',
  name,
  maxLength,
  minLength,
  error,
  onChange,
  onFirstChange,
  pattern,
  className,
  setValue,
  disabled,
  content,
  readOnly,
  description,
  alert,
  hideEye,
  customErrorContent,
  isBirthday,
  revalidateOnBlur,
  onlyFuture,
  clearErrors,
  onClick,
  autoComplete,
  hideErrorText,
}: InputProps) {
  const wrapperClassName = cn(styles.wrapper, className);
  const inputClassName = cn(styles.input, {
    [styles.error]: error,
    [styles.disabled]: disabled,
    [styles.readOnly]: readOnly,
  });
  const contentClassName = cn(styles.insideContent, {
    [styles.disabled]: disabled,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [hasFirstChange, setHasFirstChange] = useState(false);
  const translations = useTranslations();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;

    if (!hasFirstChange && onFirstChange) {
      onFirstChange(event);
      setHasFirstChange(true);
    }

    if (onChange) {
      onChange(event);
    }

    if (error && clearErrors) {
      clearErrors(name);
    }

    if (maxLength && inputValue.length > maxLength) {
      inputValue = inputValue.slice(0, maxLength);
      setValue(name, inputValue);
      return;
    }

    if (type === 'tel') {
      if (!inputValue.startsWith('+7')) {
        inputValue = '+7' + inputValue.replace(/^\+7/, '');
      }
      const formattedValue = formatPhone(inputValue);
      setValue(name, formattedValue);
      return;
    }

    if (type === 'datepicker') {
      const formattedValue = formatInputDate(inputValue);
      setValue(name, formattedValue);
      return;
    }

    if (type === 'timepicker') {
      inputValue = inputValue.replace(/[^0-9:]/g, '');
      const formattedValue = formatInputTime(inputValue);
      setValue(name, formattedValue);
      return;
    }

    setValue(name, inputValue);
  };

  const handleError = () => {
    switch (error?.type) {
      case 'required':
        return translations.fieldRequired;
      case 'pattern':
        if (type === 'password') return translations.fieldPasswordError;
        if (type === 'e-mail') return translations.fieldEmailError;
        if (type === 'tel') return translations.fieldPhoneError;
        if (type === 'datepicker' && isBirthday)
          return translations.fieldBirthdayError;
        return translations.fieldFormatError;
      case 'exists':
        return translations.fieldEmailDuplicate;
      case 'phone-exists':
        return translations.fieldPhoneDuplicate;
      case 'not-child':
        return translations.fieldBirthdayNotChild;
      case 'notFound':
        if (type === 'e-mail') return translations.fieldEmailNotFound;
        break;
      case 'validate':
        if (type === 'datepicker' && onlyFuture) {
          return 'Дата отправки не может быть ранее сегодняшнего дня';
        }
        if (type === 'time') {
          return 'Время отправки не может быть ранее текущего времени';
        }
        return '';
      case 'mismatch':
        return translations.fieldPasswordMismatch;
      default:
        return translations.fieldError;
    }
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    if (onClick) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div className={wrapperClassName}>
      <div className={styles.header}>
        <p className={styles.label}>{label}</p>
        {button && (
          <Button
            text={button.text}
            onClick={button.onClick}
            variant={Variants.Ghost}
            className={styles.button}
            tabIndex={-1}
            type="button"
          />
        )}
      </div>
      <input
        {...register(name, {
          required,
          maxLength,
          minLength,
          pattern,
        })}
        placeholder={showPassword ? '' : placeholder}
        type={type === 'password' && showPassword ? 'text' : type}
        onChange={handleChange}
        className={inputClassName}
        onClick={handleClick}
        onBlur={(event) => {
          if (revalidateOnBlur) {
            setValue(name, event.target.value, { shouldValidate: true });
          }
        }}
        readOnly={!!onClick}
        autoComplete={autoComplete}
      />
      <div className={contentClassName}>
        {content && content}
        {type === 'password' && !hideEye && (
          <img
            loading="lazy"
            src={showPassword ? '/svg/eye-closed.svg' : '/svg/eye.svg'}
            alt="show-password"
            className={styles.showPassword}
            onClick={() => setShowPassword((prev) => !prev)}
          />
        )}
      </div>
      {description && !error && (
        <p className={styles.description}>
          <span>{description}</span>
        </p>
      )}
      {alert && !error && (
        <p className={styles.alert}>
          <img loading="lazy" src="/svg/alert.svg" alt="icon" />
          <span>{alert}</span>
        </p>
      )}
      {error && !customErrorContent && !hideErrorText && (
        <p className={styles.errorTag}>{handleError()}</p>
      )}
      {error && customErrorContent && (
        <p
          className={styles.errorTag}
          dangerouslySetInnerHTML={{ __html: customErrorContent }}
        />
      )}
    </div>
  );
}
